import { Component } from "@theme/component";
import { morphSection } from "@theme/section-renderer";
import { DiscountUpdateEvent } from "@theme/events";
import { fetchConfig } from "@theme/utilities";
import { storefrontEvents } from "@theme/storefront-events";

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ["cartDiscountError", "cartDiscountErrorDiscountCode", "cartDiscountErrorShipping"];

  /** @type {AbortController | null} */
  #activeFetch = null;

  updatedCallback() {
    super.updatedCallback();

    // Cleanup any pending fetch if morph occurs during an active request
    // This ensures component is in a clean state after DOM morph
    if (this.#activeFetch) {
      this.#activeFetch.abort();
      this.#activeFetch = null;
    }

    // Reset button loading state after morph (if button exists)
    if (this.refs.cartDiscountButton) {
      this.#toggleButtonLoading(false);
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== "string") return;

    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorExists,
      cartDiscountButton,
    } = this.refs;

    if (cartDiscountButton.getAttribute("disabled")) {
      return;
    }

    cartDiscountError.classList.add("hidden");
    cartDiscountErrorDiscountCode.classList.add("hidden");
    cartDiscountErrorShipping.classList.add("hidden");
    cartDiscountErrorExists.classList.add("hidden");

    const discountCodeValue = discountCode.value.trim().toLowerCase();

    const abortController = this.#createAbortController();

    /** @type {any} */
    let discountUpdate = null;

    try {
      // "Already applied" only for a code the buyer can see applied (a pill).
      if (this.#pillDiscounts().some((code) => code.trim().toLowerCase() === discountCodeValue)) {
        this.#handleDiscountErrorExists();
        return;
      }

      // Busy right away: the request starts with the cart lookup below.
      this.#toggleButtonLoading(true);

      const existingDiscounts = await this.#existingDiscounts(abortController.signal);
      // A morph (updatedCallback) aborted this submit and reset the button meanwhile.
      if (abortController.signal.aborted) return;

      // A code the cart already holds without applying it (no pill) is submitted again, not duplicated.
      const otherDiscounts = existingDiscounts.filter((code) => code.trim().toLowerCase() !== discountCodeValue);

      // Report the whole set of codes, the new one as the buyer typed it.
      discountUpdate = this.#startDiscountUpdate([...otherDiscounts, discountCode.value.trim()]);

      const config = fetchConfig("json", {
        body: JSON.stringify({
          discount: [...otherDiscounts, discountCodeValue].join(","),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(FoxTheme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      // A code the cart won't apply still resolves; the cart reports it with `applicable: false`.
      const errorMessage = storefrontEvents.getAjaxError(data);
      storefrontEvents.resolveCart(discountUpdate, errorMessage ? null : data, errorMessage);

      this.#toggleButtonLoading(false);

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        // Shopify keeps the code on the cart: it may apply later (e.g. once a minimum is reached).
        discountCode.value = "";
        this.#handleDiscountError("discount_code");
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, "text/html");
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll(".cart-discount__pill") || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        const pillCodes = this.#pillDiscounts();
        if (
          codes.length === pillCodes.length &&
          codes.every((/** @type {string} */ code) => pillCodes.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError("shipping");
          discountCode.value = "";
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.dataset.sectionId));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      storefrontEvents.failCart(this, discountUpdate, error);
      if (error?.name !== "AbortError") this.#toggleButtonLoading(false);
    } finally {
      // Only this request\'s controller: a newer request that aborted this one keeps its own.
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      // cartPerformance.measureFromEvent("discount-update:user-action", event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== "Enter") ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== "string"
    ) {
      return;
    }

    const pill = event.target.closest(".cart-discount__pill");
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = await this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    const discountUpdate = this.#startDiscountUpdate(existingDiscounts);

    try {
      const config = fetchConfig("json", {
        body: JSON.stringify({
          discount: existingDiscounts.join(","),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(FoxTheme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const errorMessage = storefrontEvents.getAjaxError(data);
      storefrontEvents.resolveCart(discountUpdate, errorMessage ? null : data, errorMessage);

      document.dispatchEvent(new DiscountUpdateEvent(data, this.dataset.sectionId));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
      storefrontEvents.failCart(this, discountUpdate, error);
    } finally {
      // Only this request\'s controller: a newer request that aborted this one keeps its own.
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  /**
   * Opens the `shopify:cart:discount-update` event. It carries the full set of codes the cart should
   * end up with, and no `context`.
   * @param {string[]} codes
   */
  #startDiscountUpdate(codes) {
    return storefrontEvents.start(this, "CartDiscountUpdateEvent", {
      discountCodes: codes.map((code) => ({ code })),
    });
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === "discount_code" ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove("hidden");
    target.classList.remove("hidden");
  }

  #handleDiscountErrorExists() {
    const { cartDiscountErrorExists, cartDiscountError } = this.refs;
    cartDiscountError.classList.remove("hidden");
    cartDiscountErrorExists.classList.remove("hidden");
  }

  /**
   * Returns the codes of the applied discounts (rendered as pills).
   * @returns {string[]}
   */
  #pillDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    for (const pill of this.querySelectorAll(".cart-discount__pill")) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === "string") {
        discountCodes.push(pill.dataset.discountCode);
      }
    }
    return discountCodes;
  }

  /**
   * Returns every discount code on the cart: the applied ones (pills) plus codes the cart holds without
   * applying them yet (e.g. a minimum not reached, or added by an app), which have no pill. Those only exist
   * in the cart JSON (Liquid doesn't expose them).
   * @param {AbortSignal} [signal]
   * @returns {Promise<string[]>}
   */
  async #existingDiscounts(signal) {
    const codes = this.#pillDiscounts();
    const known = new Set(codes.map((code) => code.toLowerCase()));
    try {
      const cart = await fetch(FoxTheme.routes.cart, { signal }).then((response) => response.json());
      for (const { code } of cart.discount_codes ?? []) {
        if (code && !known.has(code.toLowerCase())) {
          codes.push(code);
          known.add(code.toLowerCase());
        }
      }
    } catch (error) {
      if (error?.name !== "AbortError") console.error(error);
    }
    return codes;
  }

  #toggleButtonLoading(isLoading = true) {
    const { cartDiscountButton } = this.refs;

    cartDiscountButton.classList.toggle("btn--loading", isLoading);

    if (isLoading) {
      cartDiscountButton.setAttribute("disabled", true);
    } else {
      cartDiscountButton.removeAttribute("disabled");
    }
  }
}

if (!customElements.get("cart-discount")) {
  customElements.define("cart-discount", CartDiscount);
}
